import { CommonModule } from '@angular/common';
import { Component, computed, signal, Signal, viewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { SectionComponent } from 'src/app/shared/components/section/section.component';
import { CraftCalculatorStateService } from '../craft-calculator-state.service';
import { CheckPriceButtonComponent } from 'src/app/shared/components/check-price-button/check-price-button.component';
import { CopyTextButtonComponent } from 'src/app/shared/components/copy-text-button/copy-text-button.component';
import { SubsectionComponent } from '../../../shared/components/subsection/subsection.component';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { InputItem } from 'src/app/shared/models/craft-models/craft-models';
import { SparkLineChartComponent } from 'src/app/shared/components/spark-line-chart/spark-line-chart.component';
import { formatHoursAndMinutes } from 'src/app/shared/functions/datetime-functions';
import { SmallNumberPipe } from 'src/app/pipes/small-number/small-number.pipe';
import { ItemImageComponent } from 'src/app/shared/components/item-image/item-image.component';

@Component({
  selector: 'app-craft-calculator-prices',
  imports: [
    CommonModule,
    SectionComponent,
    MatFormFieldModule,
    MatInputModule,
    FormsModule,
    MatTooltipModule,
    CheckPriceButtonComponent,
    CopyTextButtonComponent,
    SubsectionComponent,
    MatSelectModule,
    MatIconModule,
    MatButtonModule,
    MatCheckboxModule,
    MatTableModule,
    MatPaginatorModule,
    MatSortModule,
    SparkLineChartComponent,
    SmallNumberPipe,
    ItemImageComponent,
  ],
  templateUrl: './craft-calculator-prices.component.html',
  styleUrl: './craft-calculator-prices.component.scss',
})
export class CraftCalculatorPricesComponent {
  public formatHoursAndMinutes = formatHoursAndMinutes;
  public searchText = signal('');
  public sort: Signal<MatSort | undefined> = viewChild<MatSort>('sort');
  public paginator: Signal<MatPaginator | undefined> = viewChild<MatPaginator>('paginator');
  public dataSource: Signal<MatTableDataSource<InputItem>> = computed(() => {
    const ds = this.setupDataSource();
    this.craftCalculatorStateService.oldInputSort = ds.sort;
    return ds;
  });
  public displayedColumns = computed(() => {
    if (this.craftCalculatorStateService.craftGroup().showCharts()) {
      return ['item', 'amount', 'location', 'pricetype', 'selltype', 'aodpprice', 'userprice', 'chart', 'leftover'];
    }
    return ['item', 'amount', 'location', 'pricetype', 'selltype', 'aodpprice', 'userprice', 'leftover'];
  });
  constructor(public craftCalculatorStateService: CraftCalculatorStateService) {}
  private setupDataSource(): MatTableDataSource<InputItem> {
    let inputs = this.craftCalculatorStateService.craftGroup().inputItems();

    if (this.craftCalculatorStateService.craftGroup().hideUnusedItems()) {
      inputs = inputs.filter((input) => {
        return input.totalAmount() > 0;
      });
    }

    if (this.craftCalculatorStateService.craftGroup().onlyMissingPricesItems()) {
      inputs = inputs.filter((input) => input.hasMissingPrices());
    }

    if (this.searchText().trim() !== '') {
      const searchText = this.searchText().toLowerCase().trim();
      inputs = inputs.filter((input) => {
        return input.item.localizedName.toLowerCase().includes(searchText);
      });
    }

    const ds = new MatTableDataSource<InputItem>(inputs);

    const sort = this.sort();

    if (sort) {
      ds.sortingDataAccessor = (item: InputItem, property) => {
        const defaultSort = item.item.groupUniqueName.toLowerCase() + '_' + item.item.tier + '_' + item.item.enchantmentLevel;
        switch (property) {
          case 'item':
            return defaultSort;
          case 'amount':
            return item.totalAmount();
          case 'location':
            return (item.buyLocation()?.displayName?.toLowerCase() || 'zzz_no_location') + '_' + defaultSort;
          case 'pricetype':
            return (item.buyPriceType()?.friendlyName?.toLowerCase() || 'zzz_no_pricetype') + '_' + defaultSort;
          case 'selltype':
            return (item.leftOverSellPriceType()?.friendlyName?.toLowerCase() || 'zzz_no_selltype') + '_' + defaultSort;
          case 'aodpprice':
            return item.aodpPrice() || 0;
          case 'userprice':
            return item.buyUserPrice() || 0;
          case 'chart':
            return item.buyUserPrice() || 0;
          case 'leftover':
            return item.totalLeftOvers() || 0;
          default:
            return defaultSort;
        }
      };
      ds.sort = sort;

      if (this.craftCalculatorStateService.oldInputSort) {
        if (ds.sort) {
          ds.sort.active = this.craftCalculatorStateService.oldInputSort.active;
          ds.sort.direction = this.craftCalculatorStateService.oldInputSort?.direction;
        }
      }
    }

    const paginator = this.paginator();

    if (paginator) {
      ds.paginator = paginator;
    }
    return ds;
  }
}
